import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Analytics, logEvent } from '@angular/fire/analytics';
import { Observable } from 'rxjs';
import {
  catchError,
  debounceTime,
  finalize,
  timeout,
  map
} from 'rxjs/operators';
import { DialogComponent } from '../components/dialog/dialog.component';
import { UserService } from './user.service';
import { CONSTANTS } from '../constants';
import { MessageService } from 'primeng/api';
import { isCacheable } from '../models/cache.model';
import { LoadingService } from './loading.service';
import { DialogService } from 'primeng/dynamicdialog';
import { environment as ENV } from '../../environments/environment';

export const DEFAULT_TIMEOUT = 20 * 1000; // set time out same as back-end timeout

@Injectable({
  providedIn: 'root'
})
export class NetworkInterceptorService implements HttpInterceptor {
  private readonly translateService = inject(TranslateService);
  private readonly dialogService = inject(DialogService);
  private readonly userService = inject(UserService);
  private readonly router = inject(Router);
  private readonly toastService = inject(MessageService);
  private readonly loadingService = inject(LoadingService);

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    this.loadingService.show();

    request = this.addHeaders(request);

    return next.handle(request).pipe(
      timeout(DEFAULT_TIMEOUT),
      map((event: HttpEvent<any>) => {
        // Intercept HTTP responses and check if they implement Cacheable interface
        if (event instanceof HttpResponse && event.body) {
          const body = event.body;
          // Check if the response implements Cacheable interface
          if (isCacheable(body)) {
            // Show toast ONLY if cache metadata with date exists
            if (body.cache?.date) {
              const date = new Date(body.cache.date);
              this.toastService.add({
                severity: 'info',
                summary: this.translateService.instant('Cache.DefaultMsg', {
                  date: date.toLocaleString()
                }),
                key: 'global',
                life: 10000
              });
            }
            // Keep only the results array in the response body (even if cache null)
            return event.clone({
              body: body.results
            });
          }
        }
        return event;
      }),
      catchError(async (error) => {
        const status = error.status;
        const errorUrl = status === 0 ? error.url : error.error.url;

        // Controlled errors skip the default error dialog
        if (
          Object.values(CONSTANTS.CONTROLLED_ERRORS).includes(error.error.error)
        ) {
          throw error; // continue with the error handling
        }

        const customError = error.error.error
          ? `${this.translateService.instant('Errors.CodError')}: ${
              error.error.error
            }`
          : '';

        const description = error.error.message
          ? `${error.error.message}`
          : `${error.message}`;
        this.dialogService.open(DialogComponent, {
          data: {
            description,
            customError
          },
          header: `${this.translateService.instant(
            'Errors.InternalError'
          )} ${status}`,
          width: '400px',
          closable: true,
          autoZIndex: false
        });

        // Register custom events to Google Analytics
        if (location.hostname !== 'localhost') {
          const user = this.userService.getMe();
          const newError = {
            page: this.router.url,
            user: user.employeeId,
            endpoint: errorUrl,
            codError: error.error.codError || status,
            message: error.error.message || error.message
          };
          const gaService = inject(Analytics);
          logEvent(gaService, 'http_error', newError);
        }
        throw error;
      }),
      debounceTime(1000) as any,
      finalize(() => {
        this.loadingService.hide();
      })
    );
  }

  private addHeaders<T>(existing: HttpRequest<T>): HttpRequest<T> {
    const finalHeaders = existing.headers
      .append(CONSTANTS.TRACKING_HEADERS.CLIENT_APPLICATION, 'sstBackoffice')
      .append(CONSTANTS.TRACKING_HEADERS.SST_PLATFORM, 'Web')
      .append(CONSTANTS.TRACKING_HEADERS.SST_VERSION, ENV.appVersion);

    return existing.clone({ headers: finalHeaders });
  }
}
